/* @flow */
/**
 * Stores PO search criteria, filters, result pages, saved-search metadata, and thunk workflows for
 * searching, filtering, pagination, bookmarks, exports, and lookup data.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import request from '../../services/request';
import {
  mapInputFieldsToRequest,
  getFilterValuesFromResponse,
  getFiltersInResponseRequestBody,
  mapFiltersToRequest,
  mapRequestToInputFields,
  setFieldItemVas,
  setFilterAsiaSize,
  setValidFields
} from '../../helpers/dataHelpers';
import serviceAPI from '../../helpers/serviceAPI';
import { newContractPOSearchDataMock } from '../mock/POSearchDataMock';
import {
  searchRequestTemplate,
  poSearchCriteriaFields,
  initialSearchFieldsState,
  initialPOSearchFiltersState
} from '../../constants/staticData';
import { toggleSpinner, togglePaginationSpinner, updateDialogStates } from '../dialogStates/dialogStatesSlice';
import getflattenResponse, { cryptoRand } from '../../helpers/common';
import
dataTableHeader,
{ dataTableHeaderInactv, dataTableCustomView } from '../../constants/dataTableHeader';
import poSearchFieldMapping from '../../constants/fieldMappings/poSearchFieldMapping';
import { settingsActions } from '../profileSettings/settingSlice';
import {
  setFieldSrcSysNm,
  getFieldsForRequest,
  getSelectedFilterValues,
  getModifiedSelectedFilters,
  getSelectedFilterKeys,
  getFilterInResponseValueInPOSearch,
  getFiltersInResponse,
  getFilterData,
  sortDataAscending,
  setFieldAsiaSize,
  removeFieldAsiaSize,
  setFieldMfgCode,
  removeFieldMfgCode
} from './poSearchHelper';
import appConfig from '../../appConfig';
import poSearchFieldProperties from '../../constants/fieldProperties/poSearchFieldProperties';

const name = 'PoSearchResult';

function createInitialState() {
  return {
    PoSearchResult: {},
    PoSearchResultNext: {},
    PoSearchResultPrev: {},
    poSearchFilters: [],
    anchor: 0,
    nextAnchor: 0,
    prevAnchor: 0,
    poSearchResultTotalCount: 0,
    searchedValue: '',
    error: '',
    spinner: false,
    showSearch: false,
    searchedPoCategory: '',
    selectedRecordsCount: 0,
    searchId: '',
    searchPanelId: '',
    searchRequestData: {
      ...initialSearchFieldsState
    },
    page: 0,
    rowsPerPage: 100,
    key: '',
    savedSearchName: '',
    savedSearchId: '',
    isSavedSearchModified: false,
    body: null,
    selectedFields: [],
    schedulerData: {},
    filterId: 'none',
    filterDropdownValues: {},
    selectedFilters: {
      ...initialPOSearchFiltersState
    },
    jumpToPage: 0,
    apiCallAfterAppend: false,
    apiCallBeforeAppend: false,
    hasFPCAccess: false,
    isPOSearchFilterBarOpen: false
  };
}
export const initialState = createInitialState();

/**
 * Reducer that updates the active PO search page from either a direct payload or a page property.
 */
function updatePOSearchPage(state: Object, action: Object) {
  return {
    ...state,
    page: action.payload?.page ? action.payload?.page : action.payload
  };
}

/**
 * Reducer that updates the PO search row-count setting from either a direct payload or a page
 * property.
 */
function updatePOSearchRowsPerPage(state: Object, action: Object) {
  return {
    ...state,
    rowsPerPage: action.payload?.page ? action.payload?.page : action.payload
  };
}

/**
 * Reducer that stores the current PO results and the prefetched next result set with refreshed
 * totals.
 */
function PoSearchResultsNext(state: Object, action: Object) {
  const payload = {
    data: getflattenResponse(action.payload.currentResultSet),
    dataNext: getflattenResponse(action.payload.response.data)
  };
  return {
    ...state,
    PoSearchResult: payload.data,
    PoSearchResultNext: payload.dataNext,
    PoSearchResultTotalPages: payload.data?.pages?.totalPages,
    PoSearchResultTotalCount: payload.data?.pages?.totalResources
  };
}

/**
 * Reducer that advances to a prefetched next result set after jump-to-page navigation and
 * recalculates prefetch flags.
 */
function appendJumpToPageNextResultSet(state: Object, action: Object) {
  const payload = {
    dataPrev: state.PoSearchResult,
    data: state.PoSearchResultNext
  };

  // Page boundaries determine when another 1,000-row result window must be fetched.
  const numberString = state.page.toString();

  const rowPerPage = action.payload;

  let checkApiCallApend = false;
  let checkApiCallPrepend = false;

  if (Number(rowPerPage) === 100) {
    checkApiCallApend = numberString[numberString.length - 1] === '9';
    checkApiCallPrepend = numberString[numberString.length - 1] === '0';
  } else if (Number(rowPerPage) === 250) {
    checkApiCallApend = (Number(numberString) + 1) % 4 === 0;
    checkApiCallPrepend = Number(numberString) % 4 === 0;
  } else if (Number(rowPerPage) === 500) {
    checkApiCallApend = (Number(numberString) + 1) % 2 === 0;
    checkApiCallPrepend = Number(numberString) % 2 === 0;
  }

  return {
    ...state,
    PoSearchResult: payload.data,
    PoSearchResultPrev: payload.dataPrev,
    PoSearchResultNext: null,
    apiCallAfterAppend: checkApiCallApend,
    apiCallBeforeAppend: checkApiCallPrepend
  };
}

/**
 * Reducer that advances the visible PO result page group to the already prefetched next result set.
 */
function appendNextResultSet(state: Object) {
  const payload = {
    dataPrev: state.PoSearchResult,
    data: state.PoSearchResultNext
  };

  return {
    ...state,
    PoSearchResult: payload.data,
    PoSearchResultPrev: payload.dataPrev,
    PoSearchResultNext: null
  };
}

/**
 * Reducer that stores the current PO results and the prefetched previous result set with refreshed
 * totals.
 */
function PoSearchResultsPrev(state: Object, action: Object) {
  const payload = {
    data: getflattenResponse(action.payload.currentResultSet),
    dataPrev: getflattenResponse(action.payload.response.data)
  };
  return {
    ...state,
    PoSearchResult: payload.data,
    PoSearchResultPrev: payload.dataPrev,
    PoSearchResultTotalPages: payload.data?.pages?.totalPages,
    PoSearchResultTotalCount: payload.data?.pages?.totalResources,
    spinner: false
  };
}

/**
 * Reducer that moves to a prefetched previous result set after jump-to-page navigation and
 * recalculates prefetch flags.
 */
function appendJumpToPagePrevResultSet(state: Object, action: Object) {
  const payload = {
    data: state.PoSearchResultPrev,
    dataNext: state.PoSearch
  };

  const numberString = state.page.toString();

  const rowPerPage = action.payload;

  let checkApiCallApend = false;
  let checkApiCallPrepend = false;

  if (Number(rowPerPage) === 100) {
    checkApiCallApend = numberString[numberString.length - 1] === '9';
    checkApiCallPrepend = numberString[numberString.length - 1] === '0';
  } else if (Number(rowPerPage) === 250) {
    checkApiCallApend = (Number(numberString) + 1) % 4 === 0;
    checkApiCallPrepend = Number(numberString) % 4 === 0;
  } else if (Number(rowPerPage) === 500) {
    checkApiCallApend = (Number(numberString) + 1) % 2 === 0;
    checkApiCallPrepend = Number(numberString) % 2 === 0;
  }

  return {
    ...state,
    PoSearchResult: payload.data,
    PoSearchResultNext: payload.dataNext,
    PoSearchResultPrev: null,
    apiCallAfterAppend: checkApiCallApend,
    apiCallBeforeAppend: checkApiCallPrepend
  };
}

/**
 * Reducer that moves the visible PO result page group to the already prefetched previous result
 * set.
 */
function appendPrevResultSet(state: Object) {
  const payload = {
    data: state.PoSearchResultPrev,
    dataNext: state.PoSearch
  };

  return {
    ...state,
    PoSearchResultPrev: null,
    PoSearchResult: payload.data,
    PoSearchResultNext: payload.dataNext
  };
}

/**
 * Reducer that stores a fresh PO search response, resets selection counts, and clears cached
 * neighbor pages.
 */
function PoSearchResults(state: Object, action: Object) {
  const payload = {
    data: getflattenResponse(action.payload.data)
  };
  return {
    ...state,
    PoSearchResult: payload.data,
    PoSearchResultTotalPages: payload.data?.pages?.totalPages,
    PoSearchResultTotalCount: payload.data?.pages?.totalResources,
    poSearchFilters: payload.data.filters,
    selectedRecordsCount: 0,
    PoSearchResultPrev: null,
    PoSearchResultNext: null,
    spinner: false
  };
}

/**
 * Reducer that stores PO search results fetched from offset zero and refreshes result totals.
 */
function PoSearchOffset(state: Object, action: Object) {
  const payload = {
    data: getflattenResponse(action.payload.data)
  };
  return {
    ...state,
    PoSearchResult: payload.data,
    PoSearchResultTotalPages: payload?.data?.pages?.totalPages,
    PoSearchResultTotalCount: payload?.data?.pages?.totalResources,
    poSearchFilters: payload?.data?.filters,
    selectedRecordsCount: 0,
    PoSearchResultPrev: null,
    PoSearchResultNext: null,
    spinner: false
  };
}

/**
 * Reducer that records a PO search error and clears the local spinner flag.
 */
function PoSearchFailure(state: Object, action: Object) {
  return {
    ...state,
    error: action.payload,
    spinner: false
  };
}

/**
 * Reducer that toggles visibility for the PO search panel.
 */
function toggleSearch(state: Object, action: Object) {
  return {
    ...state,
    showSearch: action.payload
  };
}

/**
 * Reducer that generates a new PO search ID to force dependent views to refresh.
 */
function resetSearchId(state: Object) {
  return {
    ...state,
    searchId: cryptoRand().toString().substr(2, 8)
  };
}

/**
 * Reducer that generates a new search-panel ID to refresh panel-driven UI state.
 */
function resetSearchPanelId(state: Object) {
  return {
    ...state,
    searchPanelId: cryptoRand().toString().substr(2, 8)
  };
}

/**
 * Reducer that clears PO result data and resets paging, selected filters, and search input state.
 */
function resetPoSearchResult(state: Object) {
  return {
    PoSearchResult: {},
    PoSearchResultTotalPages: 0,
    PoSearchResultTotalCount: 0,
    poSearchFilters: {},
    selectedRecordsCount: 0,
    PoSearchResultPrev: {},
    PoSearchResultNext: {},
    page: 0,
    rowsPerPage: 100,
    searchId: '',
    searchRequestData: {
      ...initialSearchFieldsState
    },
    selectedFilters: {
      ...initialPOSearchFiltersState
    },
    selectedFields: [...new Set(state.selectedFields)]
  };
}

/**
 * Reducer that stores search-panel input data for PO search criteria.
 */
function persistSearchPanelData(state: Object, action: Object) {
  return {
    ...state,
    searchRequestData: action.payload
  };
}

/**
 * Reducer that refreshes the PO search key used to force UI rerenders.
 */
function updatePoSearchId(state: Object) {
  return {
    ...state,
    key: cryptoRand()
  };
}

/**
 * Reducer that stores the PO search request body and marks an active saved search as modified when
 * it changes.
 */
function updateSearchRequestBody(state: Object, action: Object) {
  const requestExist = JSON.stringify(state.body);
  return {
    ...state,
    isSavedSearchModified: Boolean(
      state.savedSearchName && requestExist !== JSON.stringify(action.payload)
    ),
    body: action.payload
  };
}

/**
 * Reducer that explicitly sets whether the active PO saved search has unsaved changes.
 */
function updateIsSavedSearchModified(state: Object, action: Object) {
  return {
    ...state,
    isSavedSearchModified: Boolean(action.payload)
  };
}

/**
 * Reducer that replaces the PO search body without changing saved-search modification state.
 */
function updateSearchRequestBodyOnSameBookmark(state: Object, action: Object) {
  return {
    ...state,
    body: action.payload
  };
}

/**
 * Reducer that stores the selected PO search fields with duplicate keys removed.
 */
function updatePoSearchFields(state: Object, action: Object) {
  return {
    ...state, selectedFields: [...new Set(action.payload)]
  };
}

/**
 * Reducer that updates request filter criteria and marks the saved search modified when applicable.
 */
function updateFiltersInRequest(state: Object, action: Object) {
  const updatedBody = { ...state.body, filter: action.payload };
  return { ...state, body: updatedBody, isSavedSearchModified: Boolean(state.savedSearchName) };
}

/**
 * Reducer that updates request filtersInResponse metadata used to populate filter dropdowns.
 */
function updateFiltersInResponse(state: Object, action: Object) {
  const updatedBody = { ...state.body, filtersInResponse: action.payload };
  return { ...state, body: updatedBody };
}

/**
 * Reducer that updates the request offset stored in PO search state.
 */
function updateOffsetInRequest(state: Object, action: Object) {
  const updatedBody = { ...state.body, offset: action.payload };
  return { ...state, body: updatedBody };
}

/**
 * Reducer that stores the active saved-search name and ID and clears modification tracking.
 */
function updateSavedSearchNameAndId(state: Object, action: Object) {
  return {
    ...state,
    savedSearchName: action.payload.savedSearchName,
    savedSearchId: action.payload.savedSearchId,
    isSavedSearchModified: false
  };
}

/**
 * Reducer that updates the active saved-search name and clears modification tracking.
 */
function updateSavedSearchName(state: Object, action: Object) {
  return {
    ...state, savedSearchName: action.payload, isSavedSearchModified: false
  };
}

/**
 * Reducer that clears saved-search metadata and request body for PO search.
 */
function resetPOSearchRequest() {
  return {
    savedSearchName: '',
    savedSearchId: '',
    isSavedSearchModified: false,
    body: null,
    selectedFields: [],
    schedulerData: {}
  };
}

/**
 * Reducer that clears PO saved-search request data while preserving the supplied selected-field
 * list.
 */
function resetPOSearchRequestDataOnClearSearch(state: Object, action: Object) {
  const clearSearchSelectedField = action.payload.payload;
  return {
    savedSearchName: '',
    savedSearchId: '',
    isSavedSearchModified: false,
    body: null,
    selectedFields: [...clearSearchSelectedField],
    selectedFilters: {
      ...initialPOSearchFiltersState
    }
  };
}

/**
 * Reducer that stores scheduler metadata associated with the active PO saved search.
 */
function setSchedularData(state: Object, action: Object) {
  return {
    ...state,
    schedulerData: action.payload
  };
}

/**
 * Reducer that generates a new filter ID to refresh filter UI state.
 */
function setFilterId(state: Object) {
  return { ...state, filterId: cryptoRand().toString().substr(2, 8) };
}

/**
 * Reducer that enables a selected filter key by initializing it with an empty value list.
 */
function addNewFilter(state: Object, action: Object) {
  if (!state.selectedFilters[action.payload] && action.payload) {
    return {
      ...state,
      selectedFilters: {
        ...state.selectedFilters,
        [action.payload]: []
      }
    };
  }
  return { ...state };
}

/**
 * Reducer that disables a selected filter key by setting it to null.
 */
function removeFilter(state: Object, action: Object) {
  if (state.selectedFilters[action.payload] && action.payload) {
    const selectedFilters = { ...state.selectedFilters };
    selectedFilters[action.payload] = null;
    return { ...state, selectedFilters };
  }
  return { ...state };
}

/**
 * Reducer that adds one value to an enabled PO search filter without duplicating it.
 */
function addFilterValue(state: Object, action: Object) {
  if (
    action.payload.filter
    && action.payload.value
    && state.selectedFilters[action.payload.filter]
  ) {
    const values = [...state.selectedFilters[action.payload.filter]];
    if (!values.includes(action.payload.value)) {
      values.push(action.payload.value);
    }
    return {
      ...state,
      selectedFilters: {
        ...state.selectedFilters,
        [action.payload.filter]: values
      }
    };
  }
  return { ...state };
}

/**
 * Reducer that removes one value from an enabled PO search filter.
 */
function removeFilterValue(state: Object, action: Object) {
  if (
    action.payload.filter
    && action.payload.value
    && state.selectedFilters[action.payload.filter]
  ) {
    const values = [...state.selectedFilters[action.payload.filter] || []];
    const indexOfValue = values.indexOf(action.payload.value);
    if (indexOfValue > -1) {
      values.splice(indexOfValue, 1);
    }
    return {
      ...state,
      selectedFilters: {
        ...state.selectedFilters,
        [action.payload.filter]: values
      }
    };
  }
  return { ...state };
}

/**
 * Reducer that replaces all selected values for a PO search filter.
 */
function addAllFilterValues(state: Object, action: Object) {
  if (Array.isArray(action.payload.values) && action.payload.filter) {
    return {
      ...state,
      selectedFilters: {
        ...state.selectedFilters,
        [action.payload.filter]: action.payload.values
      }
    };
  }
  return { ...state };
}

/**
 * Reducer that stores sorted filter dropdown values returned from the PO search API.
 */
function updateFilterDropdownValues(state: Object, action: Object) {
  if (
    action.payload
    && Object.keys(action.payload).length > 0
  ) {
    return {
      ...state,
      filterDropdownValues: sortDataAscending(action.payload)
    };
  }
  return { ...state };
}

/**
 * Reducer that restores PO filters and dropdowns to defaults while preserving selected fields.
 */
function resetFiltersToInitialState(state: Object) {
  return {
    ...state,
    filterId: 'none',
    filterDropdownValues: {},
    selectedFilters: {
      ...initialPOSearchFiltersState
    },
    selectedFields: [...new Set(state.selectedFields)]
  };
}

/**
 * Reducer that clears values for enabled filters and keeps disabled filters as null.
 */
function resetSelectedFiltersToInitialState(state: Object) {
  const selectedFilters = {};
  Object.keys(state.selectedFilters).forEach((filter) => {
    if (state.selectedFilters[filter]) {
      selectedFilters[filter] = [];
    } else {
      selectedFilters[filter] = null;
    }
  });
  return {
    ...state,
    selectedFilters
  };
}

/**
 * Reducer that disables all PO search filters and clears dropdown values.
 */
function clearFilters(state: Object) {
  const selectedFilters = {};
  Object.keys(state.selectedFilters).forEach((filter) => {
    selectedFilters[filter] = null;
  });
  return {
    ...state,
    filterDropdownValues: {},
    selectedFilters
  };
}

/**
 * Reducer that enables every known PO search filter with an empty value list.
 */
function addAllFilters(state: Object) {
  const selectedFilters = {};
  Object.keys(state.selectedFilters).forEach((filter) => {
    if (!selectedFilters[filter]) {
      selectedFilters[filter] = [];
    }
  });
  return {
    filterDropdownValues: {},
    selectedFilters
  };
}

/**
 * Reducer that bulk-enables selected filters from a list or merges an incoming selected-filter map.
 */
function bulkUpdateSelectedFilters(state: Object, action: Object) {
  if (Array.isArray(action.payload)) {
    const modifiedSelectedFilters = { ...state.selectedFilters };
    Object.keys(modifiedSelectedFilters).forEach((filter: string) => {
      if (action.payload.includes(filter)) {
        if (!modifiedSelectedFilters[filter]) modifiedSelectedFilters[filter] = [];
      } else {
        modifiedSelectedFilters[filter] = null;
      }
    });
    return { ...state, selectedFilters: modifiedSelectedFilters };
  }
  if ((typeof action.payload === 'object') && action.payload.selectedFilters) {
    const modifiedSelectedFilters = {
      ...state.selectedFilters,
      ...action.payload.selectedFilters
    };
    return { ...state, selectedFilters: modifiedSelectedFilters };
  }
  return { ...state };
}

/**
 * Reducer that clears the cached next PO result set.
 */
function updateNextResultSettoNull(state: Object) {
  return {
    ...state,
    PoSearchResultNext: null
  };
}

/**
 * Reducer that clears the cached previous PO result set.
 */
function updatePreviousResultSettoNull(state: Object) {
  return {
    ...state,
    PoSearchResultPrev: null
  };
}

/**
 * Reducer that stores the requested jump-to page and flags pagination thunks to honor it.
 */
function updateJumpToPage(state: Object, action: Object) {
  return {
    ...state,
    jumpToPage: action.payload,
    triggerJumpToPage: true
  };
}

/**
 * Reducer that toggles the jump-to-page trigger consumed by pagination thunks.
 */
function updateTriggerJumpToPage(state: Object, action: Object) {
  return {
    ...state,
    triggerJumpToPage: action.payload
  };
}

/**
 * Reducer that tracks whether appending the next page group requires another API call.
 */
function updateApiCallAfterAppend(state: Object, action: Object) {
  return {
    ...state,
    apiCallAfterAppend: action.payload
  };
}

/**
 * Reducer that tracks whether prepending the previous page group requires another API call.
 */
function updateApiCallBeforePrepend(state: Object, action: Object) {
  return {
    ...state,
    apiCallBeforeAppend: action.payload
  };
}

/**
 * Reducer that stores whether the user can access FPC document types.
 */
function updateHasFPCAccess(state: Object, action: Object) {
  return {
    ...state,
    hasFPCAccess: action.payload
  };
}

/**
 * Reducer that stores sorted tier-2 vendor options for PO search filters.
 */
function updateTier2VendorList(state: Object, action: Object) {
  return {
    ...state,
    tier2VendorList: action.payload
  };
}

/**
 * Reducer that stores whether the PO search filter bar is open.
 */
function updatePOSearchIsFilterBarOpen(state: Object, action: Object) {
  return {
    ...state,
    isPOSearchFilterBarOpen: action.payload
  };
}

/**
 * Builds the PO search reducer map registered with Redux Toolkit.
 */
function createReducers() {
  return {
    updatePOSearchPage,
    updatePOSearchRowsPerPage,
    PoSearchResultsNext,
    appendNextResultSet,
    PoSearchResultsPrev,
    appendPrevResultSet,
    PoSearchResults,
    PoSearchOffset,
    PoSearchFailure,
    toggleSearch,
    resetSearchId,
    resetSearchPanelId,
    resetPoSearchResult,
    persistSearchPanelData,
    updatePoSearchId,
    updateSearchRequestBody,
    updateIsSavedSearchModified,
    updateSearchRequestBodyOnSameBookmark,
    updatePoSearchFields,
    updateFiltersInRequest,
    updateFiltersInResponse,
    updateOffsetInRequest,
    updateSavedSearchNameAndId,
    updateSavedSearchName,
    resetPOSearchRequest,
    resetPOSearchRequestDataOnClearSearch,
    setSchedularData,
    setFilterId,
    addNewFilter,
    removeFilter,
    addFilterValue,
    removeFilterValue,
    addAllFilterValues,
    updateFilterDropdownValues,
    resetFiltersToInitialState,
    resetSelectedFiltersToInitialState,
    clearFilters,
    addAllFilters,
    bulkUpdateSelectedFilters,
    appendJumpToPageNextResultSet,
    appendJumpToPagePrevResultSet,
    updateJumpToPage,
    updateTriggerJumpToPage,
    updateApiCallAfterAppend,
    updateApiCallBeforePrepend,
    updatePreviousResultSettoNull,
    updateNextResultSettoNull,
    updateHasFPCAccess,
    updateTier2VendorList,
    updatePOSearchIsFilterBarOpen
  };
}

export const reducers = createReducers();
export const slice = createSlice({ name, initialState, reducers });

export const actions = { ...slice.actions };

/**
 * Returns a thunk that reruns the current PO search without filters and resets filter-related
 * state.
 */
function fetchUnfilteredPOResults() {
  return createAsyncThunk(
    `${name}/fetchUnfilteredPOResults`,
    async (callback, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));
      const state = getState();
      let requestBody = {
        ...state.PoSearch.body,
        filter: [],
        offset: '0'
      };
      requestBody = setFieldSrcSysNm(requestBody);
      requestBody = setFieldItemVas(requestBody);
      requestBody = setFieldAsiaSize(requestBody);
      requestBody = setFilterAsiaSize(requestBody);
      requestBody = setFieldMfgCode(requestBody);
      request({
        api: 'POSearch',
        method: 'post',
        data: requestBody,
        cancellable: true
      }, dispatch, getState).then((response: Object) => {
        dispatch(toggleSpinner(false));
        dispatch(actions.PoSearchResults(response));
        dispatch(actions.clearFilters());
        dispatch(actions.resetSearchPanelId());
        dispatch(actions.updateFiltersInRequest([]));
        if (callback) {
          callback(response);
        }
      }).catch((error: Object) => {
        if (callback) {
          callback(null, error);
        }
        dispatch(toggleSpinner(false));
      });
    }
  );
}

/**
 * Returns a thunk that applies selected PO filters, optionally requests fresh filter dropdown
 * values, and stores the filtered results.
 */
function applyFilters() {
  return createAsyncThunk(
    `${name}/applyFilters`,
    async ({
      payload,
      applyFiltersCallback,
      shouldRequestFilterValues = false
    }, { getState, dispatch }) => {
      const state = getState();
      const selectedFilters = {
        ...state.PoSearch.selectedFilters,
        [payload.filter]: payload.values
      };
      // Convert UI-selected filter values into API filter clauses.
      const filtersRequest = mapFiltersToRequest(selectedFilters);
      dispatch(toggleSpinner(true));
      let requestBody = {
        ...state.PoSearch.body,
        filter: filtersRequest,
        offset: '0'
      };
      const selectedFilterKeys = Object.keys(selectedFilters)
        .filter((filter) => selectedFilters[filter]);
      // Request dropdown values only for enabled filters when the caller needs refreshed options.
      if (shouldRequestFilterValues && selectedFilterKeys.length > 0) {
        requestBody.filtersInResponse = getFilterInResponseValueInPOSearch(selectedFilterKeys);
      } else {
        delete requestBody.filtersInResponse;
      }
      requestBody = setFieldSrcSysNm(requestBody);
      requestBody = setFieldItemVas(requestBody);
      requestBody = setFieldAsiaSize(requestBody);
      requestBody = setFilterAsiaSize(requestBody);
      requestBody = setFieldMfgCode(requestBody);
      request({
        api: 'POSearch',
        method: 'post',
        data: requestBody,
        cancellable: true
      }, dispatch, getState).then((response: Object) => {
        dispatch(toggleSpinner(false));
        dispatch(actions.PoSearchResults(response));
        dispatch(actions.addAllFilterValues(payload));
        dispatch(actions.updateOffsetInRequest('0'));
        dispatch(actions.updateFiltersInRequest(filtersRequest));
        dispatch(actions.setFilterId());
        if (shouldRequestFilterValues && selectedFilterKeys.length > 0) {
          dispatch(actions.updateFiltersInResponse(requestBody.filtersInResponse));
        }
        if (response.data.filters) {
          dispatch(actions.updateFilterDropdownValues(
            getFilterValuesFromResponse(response.data.filters)
          ));
        }
        if (applyFiltersCallback) {
          applyFiltersCallback(response);
        }
      }).catch((error: Object) => {
        if (applyFiltersCallback) {
          applyFiltersCallback(null, error);
        }
        dispatch(toggleSpinner(false));
      });
    }
  );
}

/**
 * Invokes the new-filter request callback with an error when the filter request fails.
 */
function searchNewFilterRequestCallback(requestNewFiltersCallback, error) {
  if (requestNewFiltersCallback) {
    requestNewFiltersCallback(null, error);
  }
}

/**
 * Returns a thunk that updates selected filter keys, fetches needed dropdown values, reapplies
 * changed filters, and stores results.
 */
function requestForNewFilters() {
  return createAsyncThunk(
    `${name}/requestForNewFilters`,
    async ({
      filtersSelected,
      enableCheckboxForDefault,
      requestNewFiltersCallback
    }, { getState, dispatch }) => {
      const state = getState();
      const {
        selectedFilters,
        filterDropdownValues
      } = { ...state.PoSearch };
      const modifiedSelectedFilters = { ...selectedFilters };
      Object.keys(modifiedSelectedFilters).forEach((filter: string) => {
        getModifiedSelectedFilters(filtersSelected, filter, modifiedSelectedFilters);
      });

      const selectedFilterData = {
        selectedFilterListForPOSearch: filtersSelected
      };
      let requestBody = { ...state.PoSearch.body };
      const searchFiltersToBeRequested = [];
      const filtersInResponse = [];
      const selectedFilterKeys = Object.keys(modifiedSelectedFilters)
        .filter((filter) => modifiedSelectedFilters[filter]);
      // Track filters with selected values to decide whether the API filter clause must be rebuilt.
      const appliedFiltersKeys = new Set(
        selectedFilterKeys.filter((filter) => (
          modifiedSelectedFilters[filter] && modifiedSelectedFilters[filter].length > 0
        ))
      );
      let searchFiltersToBeApplied = null;
      const hasAppliedSearchFiltersChanged = (!(requestBody.filter || []).every((filter) => (
        appliedFiltersKeys.has(filter.fieldName)
      )));

      // Rebuild applied filters when a previously active filter is unchecked.
      if (hasAppliedSearchFiltersChanged) {
        searchFiltersToBeApplied = mapFiltersToRequest(modifiedSelectedFilters);
        requestBody.filter = [...searchFiltersToBeApplied];
      }

      selectedFilterKeys.forEach((filter) => {
        getSelectedFilterKeys(
          filter,
          modifiedSelectedFilters,
          filterDropdownValues,
          searchFiltersToBeRequested,
          filtersInResponse
        );
      });

      if (searchFiltersToBeRequested.length === 0) {
        delete requestBody.filtersInResponse;
      } else {
        requestBody.filtersInResponse = searchFiltersToBeRequested;
      }
      requestBody.offset = '0';
      if (enableCheckboxForDefault) {
        dispatch(settingsActions.userProfileSettings({
          requestBody: selectedFilterData
        }));
      }
      requestBody = setFieldSrcSysNm(requestBody);
      requestBody = setFieldItemVas(requestBody);
      requestBody = setFieldAsiaSize(requestBody);
      requestBody = setFilterAsiaSize(requestBody);
      requestBody = setFieldMfgCode(requestBody);
      dispatch(toggleSpinner(true));
      request({
        api: 'POSearch',
        method: 'post',
        data: requestBody,
        cancellable: true
      }, dispatch, getState).then((response: Object) => {
        dispatch(toggleSpinner(false));
        dispatch(actions.updatePOSearchPage(0));
        dispatch(actions.setFilterId());
        dispatch(actions.resetSearchPanelId());
        dispatch(actions.updateOffsetInRequest('0'));
        dispatch(actions.PoSearchResults(response));
        dispatch(actions.bulkUpdateSelectedFilters(filtersSelected));
        if (response.data.filters) {
          dispatch(actions.updateFilterDropdownValues({
            ...filterDropdownValues,
            ...getFilterValuesFromResponse(response.data.filters)
          }));
          dispatch(actions.updateFiltersInResponse(filtersInResponse));
        }
        if (hasAppliedSearchFiltersChanged) {
          dispatch(actions.updateFiltersInRequest(searchFiltersToBeApplied));
          dispatch(actions.PoSearchResults(response));
          dispatch(actions.updatePOSearchPage(0));
        }
        if (requestNewFiltersCallback) {
          requestNewFiltersCallback(response);
        }
      }).catch((error: Object) => {
        console.log('error');
        dispatch(toggleSpinner(false));
        searchNewFilterRequestCallback(requestNewFiltersCallback, error);
      });
    }
  );
}

/**
 * Returns a thunk that prefetches the next PO result set for jump-to-page navigation using the
 * response cursor offset.
 */
function fetchPoSearchResultsJumpToPageNextSet() {
  return createAsyncThunk(
    `${name}/fetchPoSearchResultsJumpToPageNextSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      dispatch(togglePaginationSpinner({ isFetchingNextData: true }));
      dispatch(actions.updateApiCallAfterAppend(false));
      if (state.PoSearch?.PoSearchResult?.pages?.next) {
        const currentResultSet = state.PoSearch.PoSearchResult;
        let updatedRequest = state.PoSearch.body;
        updatedRequest = setFieldSrcSysNm(updatedRequest);
        updatedRequest = setFieldItemVas(updatedRequest);
        updatedRequest = setFieldAsiaSize(updatedRequest);
        updatedRequest = setFilterAsiaSize(updatedRequest);
        updatedRequest = setFieldMfgCode(updatedRequest);
        // The API next cursor contains the offset before the first comma.
        const searchResultNextOffset = state.PoSearch.PoSearchResult.pages.next;
        const resNext = searchResultNextOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: searchResultNextOffset.slice(7, resNext)
        };
        const requestBody = { ...updatedRequest };
        delete requestBody.filtersInResponse;
        request({
          api: 'POSearch',
          method: 'post',
          data: requestBody
        }, dispatch, getState).then((response: Object) => {
          updatedRequest = removeFieldAsiaSize(updatedRequest);
          updatedRequest = removeFieldMfgCode(updatedRequest);
          dispatch(actions.updateSearchRequestBodyOnSameBookmark(updatedRequest));
          dispatch(actions.PoSearchResultsNext({ response, currentResultSet }));
          dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
        }).catch((error: Object) => {
          console.log('Error: ', error);
          dispatch(toggleSpinner(false));
          dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
        });
      }
    }
  );
}

/**
 * Returns a thunk that fetches the next PO result page group, including jump-to-page offset
 * handling.
 */
function fetchPoSearchResultsNextSet() {
  return createAsyncThunk(
    `${name}/fetchPoSearchResultsNextSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      dispatch(togglePaginationSpinner({ isFetchingNextData: true }));
      if (state.PoSearch?.PoSearchResult?.pages?.next) {
        const currentResultSet = state.PoSearch.PoSearchResult;
        const currentJumpToPage = state.PoSearch.jumpToPage || 0;
        // The backend returns 1,000-row windows, so rows per page determines pages per window.
        const pageGroupSize = 1000 / state.PoSearch.rowsPerPage;
        const pageGroup = currentJumpToPage > 0
          ? Math.floor(currentJumpToPage / pageGroupSize) : 0;
        const { triggerJumpToPage } = state.PoSearch;
        if (triggerJumpToPage) {
          dispatch(actions.updateTriggerJumpToPage(false));
        }
        let updatedRequest = state.PoSearch.body;
        updatedRequest = setFieldSrcSysNm(updatedRequest);
        updatedRequest = setFieldItemVas(updatedRequest);
        updatedRequest = setFieldAsiaSize(updatedRequest);
        updatedRequest = setFilterAsiaSize(updatedRequest);
        updatedRequest = setFieldMfgCode(updatedRequest);
        const searchResultNextOffset = state.PoSearch.PoSearchResult.pages.next;
        const resNext = searchResultNextOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: triggerJumpToPage
            // Jump-to-page requests use the target 1,000-row window offset instead of the cursor.
            ? (pageGroup * 1000).toString() : searchResultNextOffset.slice(7, resNext)
        };
        const requestBody = { ...updatedRequest };
        delete requestBody.filtersInResponse;
        request({
          api: 'POSearch',
          method: 'post',
          data: requestBody
        }, dispatch, getState).then((response: Object) => {
          updatedRequest = removeFieldAsiaSize(updatedRequest);
          updatedRequest = removeFieldMfgCode(updatedRequest);
          dispatch(actions.updateSearchRequestBodyOnSameBookmark(updatedRequest));
          dispatch(actions.PoSearchResultsNext({ response, currentResultSet }));
          dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
          if (triggerJumpToPage) {
            dispatch(actions.appendJumpToPageNextResultSet(
              state.PoSearch.rowsPerPage
            ));
            dispatch(actions.updatePreviousResultSettoNull());
          }
        }).catch((error: Object) => {
          console.log('Error: ', error);
          dispatch(toggleSpinner(false));
          dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
        });
      }
    }
  );
}

/**
 * Returns a thunk wrapper that dispatches the provided next-result PO pagination thunk.
 */
function PoSearchDataNextSet() {
  return createAsyncThunk(
    `${name}/PoSearchDataNextSet`,
    async (
      { poSearchfetchPoSearchResultsNextSet },
      { dispatch }
    ) => {
      dispatch(poSearchfetchPoSearchResultsNextSet());
    }
  );
}

/**
 * Returns a thunk that prefetches the previous PO result set for jump-to-page navigation using the
 * response cursor offset.
 */
function fetchPoSearchResultsJumpToPagePrevSet() {
  return createAsyncThunk(
    `${name}/fetchPoSearchResultsJumpToPagePrevSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      dispatch(actions.updateApiCallBeforePrepend(false));
      if (state.PoSearch?.PoSearchResult?.pages?.prev) {
        const currentResultSet = state.PoSearch.PoSearchResult;
        let updatedRequest = state.PoSearch.body;
        updatedRequest = setFieldSrcSysNm(updatedRequest);
        updatedRequest = setFieldItemVas(updatedRequest);
        updatedRequest = setFieldAsiaSize(updatedRequest);
        updatedRequest = setFilterAsiaSize(updatedRequest);
        updatedRequest = setFieldMfgCode(updatedRequest);
        // The API previous cursor contains the offset before the first comma.
        const searchResultPrevOffset = state.PoSearch.PoSearchResult.pages.prev;
        const res = searchResultPrevOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: searchResultPrevOffset.slice(7, res)
        };
        const requestbody = { ...updatedRequest };
        delete requestbody.filtersInResponse;
        dispatch(togglePaginationSpinner({ isFetchingPrevData: true }));
        request({
          api: 'POSearch',
          method: 'post',
          data: requestbody
        }, dispatch, getState).then((response: Object) => {
          updatedRequest = removeFieldAsiaSize(updatedRequest);
          updatedRequest = removeFieldMfgCode(updatedRequest);
          dispatch(actions.updateSearchRequestBodyOnSameBookmark(updatedRequest));
          dispatch(actions.PoSearchResultsPrev({ response, currentResultSet }));
          dispatch(toggleSpinner(false));
          dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
        }).catch((error: Object) => {
          console.log('Error: ', error);
          dispatch(toggleSpinner(false));
          dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
        });
      }
    }
  );
}

/**
 * Returns a thunk that fetches the previous PO result page group, including jump-to-page offset
 * handling.
 */
function fetchPoSearchResultsPrevSet() {
  return createAsyncThunk(
    `${name}/fetchPoSearchResultsPrevSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      dispatch(togglePaginationSpinner({ isFetchingPrevData: true }));
      if (state.PoSearch?.PoSearchResult?.pages?.prev) {
        const currentResultSet = state.PoSearch.PoSearchResult;
        const currentJumpToPage = state.PoSearch.jumpToPage || 0;
        const pageGroupSize = 1000 / state.PoSearch.rowsPerPage;
        const pageGroup = currentJumpToPage > 0
          ? Math.floor(currentJumpToPage / pageGroupSize) : 0;
        const { triggerJumpToPage } = state.PoSearch;
        if (triggerJumpToPage) {
          dispatch(actions.updateTriggerJumpToPage(false));
        }
        let updatedRequest = state.PoSearch.body;
        updatedRequest = setFieldSrcSysNm(updatedRequest);
        updatedRequest = setFieldItemVas(updatedRequest);
        updatedRequest = setFieldAsiaSize(updatedRequest);
        updatedRequest = setFilterAsiaSize(updatedRequest);
        updatedRequest = setFieldMfgCode(updatedRequest);
        const searchResultPrevOffset = state.PoSearch.PoSearchResult.pages.prev;
        const res = searchResultPrevOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: triggerJumpToPage
            ? (pageGroup * 1000).toString() : searchResultPrevOffset.slice(7, res)
        };
        const requestbody = { ...updatedRequest };
        delete requestbody.filtersInResponse;
        request({
          api: 'POSearch',
          method: 'post',
          data: requestbody
        }, dispatch, getState).then((response: Object) => {
          updatedRequest = removeFieldAsiaSize(updatedRequest);
          updatedRequest = removeFieldMfgCode(updatedRequest);
          dispatch(actions.updateSearchRequestBodyOnSameBookmark(updatedRequest));
          dispatch(actions.PoSearchResultsPrev({ response, currentResultSet }));
          dispatch(toggleSpinner(false));
          dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
          if (triggerJumpToPage) {
            dispatch(actions.appendJumpToPagePrevResultSet(
              state.PoSearch.rowsPerPage
            ));
            dispatch(actions.updateNextResultSettoNull());
          }
        }).catch((error: Object) => {
          console.log('Error: ', error);
          dispatch(toggleSpinner(false));
          dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
        });
      }
    }
  );
}

/**
 * Returns a thunk wrapper that dispatches the provided previous-result PO pagination thunk.
 */
function PoSearchDataPrevSet() {
  return createAsyncThunk(
    `${name}/PoSearchDataPrevSet`,
    async (
      {
        poSearchfetchPoSearchResultsPrevSet
      }, { dispatch }
    ) => {
      dispatch(poSearchfetchPoSearchResultsPrevSet());
    }
  );
}

/**
 * Returns a thunk that reloads PO results from offset zero for the current request body.
 */
function fetchPOSearchResultsOffset() {
  return createAsyncThunk(
    `${name}/fetchPOSearchResultsOffset`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      if (state.PoSearch?.PoSearchResult) {
        let updatedRequest = { ...state.PoSearch.body };
        updatedRequest = setFieldSrcSysNm(updatedRequest);
        updatedRequest = setFieldItemVas(updatedRequest);
        updatedRequest = setFieldAsiaSize(updatedRequest);
        updatedRequest = setFilterAsiaSize(updatedRequest);
        updatedRequest = setFieldMfgCode(updatedRequest);
        delete updatedRequest.offset;
        updatedRequest.offset = '0';
        // Pagination calls do not need filter dropdown metadata returned again.
        delete updatedRequest.filtersInResponse;
        dispatch(toggleSpinner(true));
        request({
          api: 'POSearch',
          method: 'post',
          data: updatedRequest
        }, dispatch, getState).then((response: Object) => {
          dispatch(actions.PoSearchOffset(response));
          dispatch(actions.resetSearchPanelId());
          dispatch(toggleSpinner(false));
        }).catch((error: Object) => {
          console.log('Error: ', error);
          dispatch(toggleSpinner(false));
        });
      }
    }
  );
}

/**
 * Returns a thunk wrapper that dispatches the provided offset-reset PO search thunk.
 */
function PoSearchDataOffset() {
  return createAsyncThunk(
    `${name}/PoSearchDataOffset`,
    async (
      {
        poSearchfetchPOSearchResultsOffset
      }, { dispatch }
    ) => {
      dispatch(poSearchfetchPOSearchResultsOffset());
    }
  );
}

/**
 * Returns a thunk that validates and transforms a PO search request, calls the POSearch API, and
 * stores results and request state.
 */
function callPOSearchAPI() {
  return createAsyncThunk(
    `${name}/callPOSearchAPI`,
    async ({
      requestBody, selectedFilterOptions, rowPerPageOption, callback, inactiveFeatureFields = []
    }, { getState, dispatch }) => {
      let payload = { ...requestBody };
      payload = setValidFields(
        poSearchFieldProperties, payload, dataTableHeaderInactv, inactiveFeatureFields
      );
      payload = setFieldSrcSysNm(payload);
      payload = setFieldItemVas(payload);
      payload = setFieldAsiaSize(payload);
      payload = setFilterAsiaSize(payload);
      payload = setFieldMfgCode(payload);
      if (serviceAPI('POSearch').mock) {
        dispatch(toggleSpinner(false));
        dispatch(actions.updateSearchRequestBody(payload));
        dispatch(actions.updatePOSearchRowsPerPage(rowPerPageOption));
        dispatch(actions.PoSearchResults(newContractPOSearchDataMock));
      } else {
        request({
          api: 'POSearch',
          method: 'post',
          data: payload,
          cancellable: true
        }, dispatch, getState).then((response: Object) => {
          dispatch(toggleSpinner(false));
          payload = removeFieldAsiaSize(payload);
          payload = removeFieldMfgCode(payload);
          const appliedPoSearchField = new Set(
            payload.search.map((item) => item.fieldName)
          );
            // .filter((field) => field !== poSearchFieldMapping.PO_LINE_ITEM_STATUS);
          dispatch(actions.updatePoSearchFields(poSearchCriteriaFields
            .map((field) => field.key)
            .filter((field) => appliedPoSearchField.has(field))));
          dispatch(actions.resetSelectedFiltersToInitialState());
          dispatch(actions.updateSearchRequestBody(payload));
          dispatch(actions.PoSearchResults(response));
          dispatch(actions.resetSearchId());
          dispatch(actions.updatePOSearchRowsPerPage(rowPerPageOption));
          dispatch(actions.updatePOSearchPage(0));
          dispatch(actions.updateFilterDropdownValues(
            getFilterValuesFromResponse(response.data.filters)
          ));
          if (selectedFilterOptions?.length) {
            const updatedFilterValues = response?.data?.filters.map((key) => key.filterName);
            dispatch(actions.bulkUpdateSelectedFilters(updatedFilterValues));
          }
          if (callback) {
            callback(response);
          }
        }).catch((error: Object) => {
          dispatch(toggleSpinner(false));
          if (callback) {
            callback(null, error);
          }
        });
      }
    }
  );
}

/**
 * Returns a thunk that builds a PO search request from input fields, default columns, and selected
 * filters before delegating to the search API thunk.
 */
function fetchPOSearchResults() {
  return createAsyncThunk(
    `${name}/fetchPOSearchResults`,
    async ({
      fieldData, callback, poSearchCallSearchApi, inactiveFeatureFields = []
    }, { getState, dispatch }) => {
      const searchPayload = mapInputFieldsToRequest(fieldData, 'poSearch');
      const fields = [];
      // Active PO searches exclude deleted lines unless the Cancelled status is explicitly
      // requested.
      if (fieldData['poLine.dpomItemStatus'] !== 'Cancelled') {
        searchPayload.push({
          fieldName: poSearchFieldMapping.DELETION_INDICATOR,
          function: 'NOT IS_DEFINED'
        });
      }
      dataTableHeader.forEach((headRow: Object) => {
        fields.push(headRow.primary);
        const secondaryFields = {};
        if (headRow.secondary && !secondaryFields[headRow.secondary]) {
          secondaryFields[headRow.secondary] = true;
          fields.push(headRow.secondary);
        }
        if (headRow.additionalFields && headRow.additionalFields.length > 0) {
          fields.push(...headRow.additionalFields);
        }
      });
      dataTableHeader.forEach((headRow: Object) => {
        if (headRow.includeInRequest) {
          fields.push(headRow.primary);
        }
      });
      dataTableCustomView.forEach((headerRow: Object) => {
        if (headerRow.includeInRequest) {
          fields.push(headerRow.primary);
        }
      });
      dataTableHeaderInactv.forEach((headRow: Object) => {
        fields.push(headRow.primary);
      });

      const state = getState();
      const { searchpaneldata } = state;
      const {
        selectedColumnOrderOptions,
        selectedFilterOptions,
        rowPerPageOption
      } = searchpaneldata || {};
      // Use saved column ordering when available; otherwise use the default PO result fields.
      const fieldsForRequest = getFieldsForRequest(
        state, selectedColumnOrderOptions, fields
      );
      const filterValuesOptByUser = getSelectedFilterValues(selectedFilterOptions);
      const filtersInResponse = getFiltersInResponse(
        selectedFilterOptions, filterValuesOptByUser, state
      );
      // Deduplicate filter descriptors before sending filtersInResponse.
      const filteredData = getFilterData(filtersInResponse);

      const requestBody = state.PoSearch.body
        ? {
          ...state.PoSearch.body,
          search: searchPayload,
          filter: [],
          filtersInResponse: filteredData,
          offset: '0'
        } : {
          ...searchRequestTemplate,
          fields: fieldsForRequest,
          search: searchPayload,
          filtersInResponse: filteredData
        };
      if (serviceAPI('POSearch').mock) {
        dispatch(toggleSpinner(true));
        dispatch(poSearchCallSearchApi({
          requestBody,
          selectedFilterOptions,
          rowPerPageOption,
          callback
        }));
      } else {
        dispatch(toggleSpinner(true));
        dispatch(poSearchCallSearchApi({
          requestBody,
          selectedFilterOptions,
          rowPerPageOption,
          callback,
          inactiveFeatureFields
        }));
      }
    }
  );
}

/**
 * Returns a thunk that runs a customized PO search request, updates selected fields, and
 * optionally preserves the current search ID.
 */
function fetchPOCustomizedSearchResults() {
  return createAsyncThunk(
    `${name}/fetchPOCustomizedSearchResults`,
    async ({
      updatedField,
      preserveSearch = false,
      callback
    }, { getState, dispatch }) => {
      if (typeof updatedField === 'object') {
        let updatedRequestBody = updatedField;
        updatedRequestBody = setFieldSrcSysNm(updatedRequestBody);
        updatedRequestBody = setFieldItemVas(updatedRequestBody);
        updatedRequestBody = setFieldAsiaSize(updatedRequestBody);
        updatedRequestBody = setFilterAsiaSize(updatedRequestBody);
        updatedRequestBody = setFieldMfgCode(updatedRequestBody);
        // Customized searches reset to the first result window and skip dropdown metadata.
        const requestWithoutFilters = { ...updatedRequestBody, offset: '0' };
        delete requestWithoutFilters.filtersInResponse;
        dispatch(toggleSpinner(true));
        if (serviceAPI('POSearch').mock) {
          dispatch(toggleSpinner(false));
          dispatch(actions.updateSearchRequestBody(updatedRequestBody));
          dispatch(actions.PoSearchResults(newContractPOSearchDataMock));
        } else {
          request({
            api: 'POSearch',
            method: 'post',
            data: requestWithoutFilters,
            cancellable: true
          }, dispatch, getState).then((response: Object) => {
            updatedRequestBody = removeFieldAsiaSize(updatedRequestBody);
            updatedRequestBody = removeFieldMfgCode(updatedRequestBody);
            dispatch(actions.updatePoSearchId());
            dispatch(toggleSpinner(false));
            dispatch(actions.updateOffsetInRequest('0'));
            dispatch(actions.resetSearchPanelId());
            dispatch(actions.updateSearchRequestBody(updatedRequestBody));
            const appliedPoCustomisedSearchField = new Set(
              updatedRequestBody.search.map((item) => item.fieldName)
            );
              // .filter((field) => (field !== poSearchFieldMapping.PO_LINE_ITEM_STATUS));
            dispatch(actions.updatePoSearchFields(poSearchCriteriaFields
              .map((field) => field.key)
              .filter((field) => appliedPoCustomisedSearchField.has(field))));
            dispatch(actions.PoSearchResults(response));
            if (!preserveSearch) {
              dispatch(actions.resetSearchId());
            }
            if (callback) {
              callback(response);
            }
          }).catch((error: Object) => {
            dispatch(actions.updatePoSearchId());
            dispatch(toggleSpinner(false));
            if (callback) {
              callback(null, error);
            }
          });
        }
      }
    }
  );
}

/**
 * Returns a thunk that retrieves user details from the AD security API for the supplied email ID.
 */
function getUserDetailsFromAdApi() {
  return createAsyncThunk(
    `${name}/getUserDetailsFromAdApi`,
    async (props, { getState, dispatch }) => {
      const { emailId, callback } = props;
      dispatch(toggleSpinner(true));
      request({
        api: 'ADUSERDETAILSSECURITYAPI',
        method: 'get',
        routeParams: { emailId }
      }, dispatch, getState).then((response: Object) => {
        dispatch(toggleSpinner(false));
        if (callback) {
          callback(response);
        }
      }).catch((error: Object) => {
        dispatch(toggleSpinner(false));
        if (callback) {
          callback(error);
        }
        dispatch(toggleSpinner(false));
      });
    }
  );
}

function buildSelectedFiltersFromSearchCriteria(searchCriteria, filterValuesFromResponse) {
  const selectedFilters = {};
  searchCriteria.filter.forEach((filter) => {
    const availableFilters = filterValuesFromResponse[filter.fieldName];
    selectedFilters[filter.fieldName] = filter.fieldValue
      .map((filterValue) => availableFilters?.find(
        (availableFilter) => availableFilter.includes(filterValue)
      ))
      .filter(Boolean);
  });
  return selectedFilters;
}

/**
 * Returns a thunk that executes a saved PO search, restores fields and filters into state, and
 * applies FPC restrictions when needed.
 */
function fetchSavedSearchResults() {
  return createAsyncThunk(
    `${name}/fetchSavedSearchResults`,
    async ({
      savedSearchData, callback, inactiveFeatureFields = []
    }, { getState, dispatch }) => {
      const state = getState();
      const { hasFPCAccess } = state.PoSearch;
      const { searchCriteria } = savedSearchData;
      let rowsPerPageOption;
      let filterOptions = [];
      if (state.searchpaneldata) {
        rowsPerPageOption = state.searchpaneldata?.rowPerPageOption;
        filterOptions = state.searchpaneldata?.selectedFilterOptions;
      }

      const filtersInResponse = savedSearchData.searchCriteria?.filtersInResponse
        ?? getFiltersInResponseRequestBody(initialPOSearchFiltersState);

      let requestBody = { ...savedSearchData.searchCriteria, filtersInResponse, offset: '0' };

      if (requestBody.search
        && !requestBody.search
          .find((criteria) => criteria.fieldName === poSearchFieldMapping.DELETION_INDICATOR)) {
        requestBody.search.push({
          fieldName: poSearchFieldMapping.DELETION_INDICATOR,
          function: 'NOT IS_DEFINED'
        });
      }
      requestBody = setValidFields(
        poSearchFieldProperties, requestBody, dataTableHeaderInactv, inactiveFeatureFields
      );
      requestBody = setFieldSrcSysNm(requestBody);
      requestBody = setFieldItemVas(requestBody);
      requestBody = setFieldAsiaSize(requestBody);
      requestBody = setFilterAsiaSize(requestBody);
      requestBody = setFieldMfgCode(requestBody);
      const filteredFieldsData = [...requestBody.fields].filter(
        (item) => !item.toLowerCase().includes('ratetypename')
      );
      requestBody = { ...requestBody, fields: filteredFieldsData };

      dispatch(toggleSpinner(true));
      dispatch(updateDialogStates({ executeBookmarkSpinner: true }));
      let updatedReqBody = { ...requestBody };
      // Users without FPC access must not execute saved searches constrained only to FPC document
      // types.
      if (!hasFPCAccess && requestBody.search) {
        updatedReqBody = {
          ...requestBody,
          search: requestBody.search.map((criteria) => {
            if (
              criteria.fieldName === 'poHeader.poDocTypeCode'
              && criteria.fieldValue
              && Array.isArray(criteria.fieldValue)
            ) {
              const newFieldValue = criteria.fieldValue.filter(
                (value) => value !== 'ZC01' && value !== 'ZC02'
              );

              return newFieldValue.length ? {
                ...criteria,
                fieldValue: criteria.fieldValue.filter(
                  (value) => value !== 'ZC01' && value !== 'ZC02'
                )
              } : null;
            }
            return criteria;
          }).filter(
            (criteria) => criteria && (
              !Array.isArray(criteria.fieldValue) || criteria.fieldValue.length > 0
            )
          )
        };
      }
      request({
        api: 'POSearch',
        method: 'post',
        data: { savedSearchID: savedSearchData.id, ...updatedReqBody },
        cancellable: true
      }, dispatch, getState).then((response: Object) => {
        dispatch(actions.updatePoSearchId());
        updatedReqBody = removeFieldAsiaSize(updatedReqBody);
        updatedReqBody = removeFieldMfgCode(updatedReqBody);
        dispatch(actions.updateSearchRequestBody(updatedReqBody));
        const appliedSavedSearchField = new Set(
          updatedReqBody.search.map((item) => item.fieldName)
        );
          // .filter((field) => (field !== poSearchFieldMapping.PO_LINE_ITEM_STATUS));
        dispatch(actions.updatePoSearchFields(poSearchCriteriaFields
          .map((field) => field.key)
          .filter((field) => appliedSavedSearchField.has(field))));
        dispatch(actions.persistSearchPanelData(
          mapRequestToInputFields([...updatedReqBody.search])
        ));
        dispatch(actions.PoSearchResults(response));
        dispatch(actions.resetSearchId());
        dispatch(actions.updatePOSearchRowsPerPage(rowsPerPageOption));
        dispatch(actions.updatePOSearchPage(0));
        dispatch(actions.updateSavedSearchNameAndId({
          savedSearchName: savedSearchData.name,
          savedSearchId: savedSearchData.id
        }));
        dispatch(actions.resetFiltersToInitialState());
        const filterValuesFromResponse = getFilterValuesFromResponse(response.data.filters || []);
        dispatch(actions.updateFilterDropdownValues(filterValuesFromResponse));
        if (filterOptions.length > 0) {
          dispatch(actions.bulkUpdateSelectedFilters(filterOptions));
        }
        if (searchCriteria?.filter?.length > 0) {
          dispatch(actions.bulkUpdateSelectedFilters({
            selectedFilters: buildSelectedFiltersFromSearchCriteria(
              searchCriteria,
              filterValuesFromResponse
            )
          }));
        }
        dispatch(toggleSpinner(false));
        dispatch(updateDialogStates({ executeBookmarkSpinner: false }));
        if (callback) {
          callback(response);
        }
      }).catch((error: Object) => {
        dispatch(actions.updatePoSearchId());
        dispatch(toggleSpinner(false));
        dispatch(updateDialogStates({ executeBookmarkSpinner: false }));
        if (callback) {
          callback(null, error);
        }
      });
    }
  );
}

/**
 * Returns a thunk that builds and submits a PO export request for selected rows or the current
 * search result set.
 */
function requestExportFilePath() {
  return createAsyncThunk(
    `${name}/requestExportFilePath`,
    async ({
      ids, fileFormat, callback, type, inactiveFeatureFields = []
    }, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));
      const state = getState();
      const searchRequestBody = state.PoSearch.body ?? {};
      const searchRequestFields = [...(searchRequestBody.fields || [])];
      // Export removes helper fields that are only needed to render composite table columns.
      const removeAdditionalFields = new Set([
        ...dataTableCustomView.filter((column) => column.additionalFields)
          .map((headRow) => headRow.additionalFields[0]),
        ...dataTableHeader.filter((column) => column.additionalFields)
          .map((headRow) => headRow.additionalFields[0]),
        ...dataTableHeaderInactv.filter((column) => column.primary)
          .map((headRow) => headRow.primary)
      ]);
      const filedsIncludingDuplicate = searchRequestFields.filter(
        (item) => !removeAdditionalFields.has(item)
      );
      // Duplicated helper keys stay eligible so intentionally repeated export fields are not
      // removed.
      const duplicateKeysAre = filedsIncludingDuplicate.filter(
        (keys, index) => filedsIncludingDuplicate.indexOf(keys) !== index
      );
      const fieldToExcludes = [
        ...appConfig.excludedPOFieldsInExportedFile,
        ...dataTableHeader.filter((column) => column.includeInRequest)
          .map((headRow) => headRow.primary),
        ...dataTableCustomView.filter((column) => column.includeInRequest)
          .map((headRow) => headRow.primary)
      ];
      let distinctAppConfigKeys = [];
      if (duplicateKeysAre.length === 0) {
        distinctAppConfigKeys = [...fieldToExcludes];
      } else {
        distinctAppConfigKeys = fieldToExcludes.filter((duplicated) => (
          !duplicateKeysAre.includes(duplicated)
        ));
      }
      let requestBody = {
        search: [...(searchRequestBody.search || [])],
        fields: filedsIncludingDuplicate.filter((field: string) => (
          !distinctAppConfigKeys.includes(field)
        )),
        filter: [...(searchRequestBody.filter) || []],
        fileFormat
      };
      if (ids.length > 0) {
        requestBody.search.push({
          fieldName: 'sizes.sizeId',
          operator: type === 'includes' ? '=' : '!=',
          fieldValue: ids
        });
      }
      requestBody = setValidFields(
        poSearchFieldProperties, requestBody, dataTableHeaderInactv, inactiveFeatureFields
      );
      requestBody = setFieldAsiaSize(requestBody);
      requestBody = setFilterAsiaSize(requestBody);
      requestBody = setFieldMfgCode(requestBody);
      requestBody = setFieldItemVas(requestBody);
      request({
        api: 'POExport',
        method: 'post',
        data: requestBody
      }, dispatch, getState).then((response: Object) => {
        dispatch(toggleSpinner(false));
        if (callback) {
          callback(response);
        }
      }).catch((error: Object) => {
        dispatch(toggleSpinner(false));
        if (callback) {
          callback(null, error);
        }
        dispatch(toggleSpinner(false));
      });
    }
  );
}

/**
 * Returns a thunk that loads, sorts, stores, and returns tier-2 vendor filter options.
 */
function fetchTier2VendorList() {
  return createAsyncThunk(
    `${name}/fetchTier2VendorList`,
    async (
      { callback },
      { getState, dispatch }
    ) => {
      request(
        {
          api: 'GET_TIER2_VENDOR_LIST',
          method: 'get'
        },
        getState,
        dispatch
      )
        .then((response) => {
          const tier2List = response.data.objects.map((option) => ({
            id: option.locationCode,
            value: option.locationCode
          }));
          // Sort alphabetic vendor codes before numeric codes, then compare numbers numerically.
          tier2List.sort((a, b) => {
            const idA = a.id;
            const idB = b.id;
            const aStartsWithNumber = /^\d/.test(idA);
            const bStartsWithNumber = /^\d/.test(idB);

            if (aStartsWithNumber !== bStartsWithNumber) {
              return aStartsWithNumber ? 1 : -1;
            }

            if (aStartsWithNumber) {
              const numA = Number.parseInt(idA, 10);
              const numB = Number.parseInt(idB, 10);
              if (!Number.isNaN(numA) && !Number.isNaN(numB) && numA !== numB) {
                return numA - numB;
              }
            }

            if (idA < idB) {
              return -1;
            }
            if (idA > idB) {
              return 1;
            }
            return 0;
          });
          const otherOption = { id: 'Other', value: 'Other' };
          const firstNumberIndex = tier2List.findIndex((item) => /^\d/.test(item.id));
          if (firstNumberIndex === -1) {
            tier2List.push(otherOption);
          } else {
            tier2List.splice(firstNumberIndex, 0, otherOption);
          }
          dispatch(actions.updateTier2VendorList(tier2List));
          if (callback) {
            callback(tier2List);
          }
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          if (callback) {
            callback(null, error);
          }
        });
    }
  );
}

/**
 * Builds the async PO search action map exported by this slice.
 */
function createExtraActions() {
  // searchFilter actions api calls
  return {
    fetchUnfilteredPOResults: fetchUnfilteredPOResults(),
    applyFilters: applyFilters(),
    requestForNewFilters: requestForNewFilters(),
    PoSearchDataNextSet: PoSearchDataNextSet(),
    PoSearchDataPrevSet: PoSearchDataPrevSet(),
    PoSearchDataOffset: PoSearchDataOffset(),
    fetchPoSearchResultsNextSet: fetchPoSearchResultsNextSet(),
    fetchPoSearchResultsPrevSet: fetchPoSearchResultsPrevSet(),
    fetchPOSearchResultsOffset: fetchPOSearchResultsOffset(),
    fetchPOSearchResults: fetchPOSearchResults(),
    callPOSearchAPI: callPOSearchAPI(),
    fetchPOCustomizedSearchResults: fetchPOCustomizedSearchResults(),
    getUserDetailsFromAdApi: getUserDetailsFromAdApi(),
    fetchSavedSearchResults: fetchSavedSearchResults(),
    requestExportFilePath: requestExportFilePath(),
    fetchPoSearchResultsJumpToPageNextSet: fetchPoSearchResultsJumpToPageNextSet(),
    fetchPoSearchResultsJumpToPagePrevSet: fetchPoSearchResultsJumpToPagePrevSet(),
    fetchTier2VendorList: fetchTier2VendorList()
  };
}

export const extraActions = createExtraActions();

export const poSearchResultActions = { ...actions, ...extraActions };
/**
 * Redux reducer for PO search result state, filters, saved-search metadata, and pagination caches.
 */
export const poSearchResultReducer = slice.reducer;
